import type { InternalRefetchQueriesInclude } from "@apollo/client";
import { useMutation } from "@apollo/client";
import AddReaction from "components/reactions/mutations/AddReaction";
import RemoveReaction from "components/reactions/mutations/RemoveReaction";
import type {
  IAddReactionMutation,
  IAddReactionMutationVariables,
} from "components/reactions/mutations/__generated__/AddReaction.generated";
import type {
  IRemoveReactionMutation,
  IRemoveReactionMutationVariables,
} from "components/reactions/mutations/__generated__/RemoveReaction.generated";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/Popover";
import Twemoji from "components/ui/Twemoji";
import classNames from "components/ui/classNames";
import type { IReactableFieldsFragment } from "lib/graphql/fragments/__generated__/ReactableFields.generated";
import { ThumbsUpIcon } from "lucide-react";
import type { IReactionGroup, IReactionSubject } from "types/graphqlTypes";
import { IReactionContent } from "types/graphqlTypes";

interface PostReactions {
  subjectId: string;
  subjectType: IReactionSubject;
  reactable: IReactableFieldsFragment;
  refetchQueries?: InternalRefetchQueriesInclude;
}

export default function PostReactions({ subjectId, subjectType, reactable, refetchQueries }: PostReactions) {
  const { reactionGroups, viewerCanReact } = reactable;

  const [addReaction, { loading: isAddingReaction }] = useMutation<IAddReactionMutation, IAddReactionMutationVariables>(
    AddReaction,
    {
      refetchQueries,
    },
  );

  const [removeReaction, { loading: isRemovingReaction }] = useMutation<
    IRemoveReactionMutation,
    IRemoveReactionMutationVariables
  >(RemoveReaction, {
    refetchQueries,
  });

  const isLoading = isAddingReaction || isRemovingReaction;

  function handleReactionClick(content: IReactionContent, reaction?: IReactionGroup) {
    if (reaction && reaction.viewerHasReacted) {
      removeReaction({
        variables: {
          input: {
            content,
            subjectId,
            subjectType,
          },
        },
      });
    } else {
      addReaction({
        variables: {
          input: {
            content,
            subjectId,
            subjectType,
          },
        },
      });
    }
  }

  if (!viewerCanReact && reactionGroups.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-x-1.5 gap-y-1.5">
      {viewerCanReact && (
        <Popover>
          <PopoverTrigger
            disabled={isLoading}
            className={classNames(
              "group rounded-2xl border border-neutral-200 p-1.5 px-2",
              "text-slate-500 hover:border-blue-50 hover:bg-blue-50 hover:text-blue-600",
              "dark:border-slate-950 dark:bg-slate-950 dark:text-slate-400 dark:hover:opacity-80",
              "dark:data-[state=open]:bg-violet-600/20 dark:data-[state=open]:text-violet-100",
              "dark:hover:bg-violet-600/20 dark:hover:text-violet-300",
            )}
          >
            <ThumbsUpIcon absoluteStrokeWidth={true} strokeWidth={2} className="size-5 stroke-current" />
          </PopoverTrigger>
          <PopoverContent
            side="top"
            align="start"
            className="flex space-x-0.5 rounded-xl border-slate-300 p-0.5 dark:shadow dark:shadow-slate-950"
          >
            {EMOJIS.sort((a, b) => (a.key < b.key ? 1 : -1)).map((emoji) => {
              const reaction = reactionGroups.find((node) => node.content === emoji.key);

              return (
                <button
                  key={emoji.key}
                  type="button"
                  disabled={isLoading}
                  onClick={() => handleReactionClick(emoji.key, reaction)}
                  className={classNames(
                    "rounded-lg border-transparent p-2 px-3 squircle hover:bg-blue-50 dark:hover:bg-slate-950",
                    {
                      "bg-slate-100 dark:bg-blue-600/20": reaction && reaction.viewerHasReacted,
                    },
                  )}
                >
                  {emoji.renderIcon()}
                </button>
              );
            })}
          </PopoverContent>
        </Popover>
      )}

      {EMOJIS.sort((a, b) => (a.key < b.key ? 1 : -1)).map((emoji) => {
        const reaction = reactionGroups.find((node) => node.content === emoji.key);
        if (!reaction) {
          return null;
        }

        return (
          <button
            key={emoji.key}
            type="button"
            disabled={isLoading || !viewerCanReact}
            className={classNames(
              "flex items-center space-x-2 rounded-xl px-3 py-1.5 text-sm text-slate-600 dark:text-slate-300",
              {
                "bg-slate-100 text-slate-600 dark:bg-blue-600/10 dark:text-blue-300": reaction.viewerHasReacted,
              },
            )}
            onClick={() => handleReactionClick(emoji.key, reaction)}
          >
            {emoji.renderIcon()}
            <span className="font-bold">{reaction.totalReactions}</span>
          </button>
        );
      })}
    </div>
  );
}

export const EMOJIS = [
  {
    key: IReactionContent.ThumbsUp,
    renderIcon: () => <Twemoji width={12} height={12} emoji="👍" />,
  },
  {
    key: IReactionContent.Laugh,
    renderIcon: () => <Twemoji width={12} height={12} emoji="😄" />,
  },
  {
    key: IReactionContent.Hooray,
    renderIcon: () => <Twemoji width={12} height={12} emoji="🎉" />,
  },
  {
    key: IReactionContent.Confused,
    renderIcon: () => <Twemoji width={12} height={12} emoji="😕" />,
  },
  {
    key: IReactionContent.Heart,
    renderIcon: () => <Twemoji width={12} height={12} emoji="❤️️" />,
  },

  {
    key: IReactionContent.Rocket,
    renderIcon: () => <Twemoji width={12} height={12} emoji="🚀" />,
  },
  {
    key: IReactionContent.Eyes,
    renderIcon: () => <Twemoji width={12} height={12} emoji="👀" />,
  },
];
